import i18n from "../i18n";

const ISO_DATE_TIME_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?(?:Z|([+-])(\d{2}):?(\d{2}))?$/;

function isValidDate(date: Date): boolean {
  return Number.isFinite(date.getTime());
}

function normalizeApiDateString(value: string): string {
  // Convert a space-separated datetime ("2026-06-26 00:22:04") to ISO 'T' form.
  let normalized = value.includes("T") ? value : value.replace(" ", "T");
  // The backend serializes java.time.LocalDateTime with no zone designator
  // (e.g. "2026-06-26T00:22:04.190") and records it in UTC on the server. If the string
  // has a time component but no trailing Z / ±hh:mm offset, tag it as UTC so the instant
  // is correct; the format helpers' toLocale* calls then render it in each user's own
  // browser timezone.
  const hasTime = /\d{2}:\d{2}/.test(normalized);
  const hasZone = /[zZ]$/.test(normalized) || /[+-]\d{2}:?\d{2}$/.test(normalized);
  if (hasTime && !hasZone) {
    normalized += "Z";
  }
  return normalized;
}

function parseArrayDate(parts: unknown[]): Date | null {
  if (parts.length < 3) return null;

  const numbers = parts.map((part) =>
    typeof part === "number" ? part : Number(part),
  );
  if (numbers.some((num) => Number.isNaN(num))) return null;

  const [year, month, day, hour = 0, minute = 0, second = 0, nano = 0] =
    numbers;
  if (![year, month, day].every((num) => Number.isFinite(num))) return null;

  const ms = Number.isFinite(nano) ? Math.floor(nano / 1_000_000) : 0;
  // Array form comes from the backend (UTC wall-clock components) — build a UTC instant.
  const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second, ms));
  return isValidDate(date) ? date : null;
}

function parseIsoLike(value: string): Date | null {
  const match = ISO_DATE_TIME_RE.exec(value);
  if (!match) return null;

  const [, y, m, d, hh, mm, ss, fraction, tzSign, tzHour, tzMinute] = match;
  const year = Number(y);
  const month = Number(m) - 1;
  const day = Number(d);
  const hour = Number(hh || 0);
  const minute = Number(mm || 0);
  const second = Number(ss || 0);
  const ms = fraction ? Math.floor(Number(`0.${fraction}`) * 1000) : 0;

  if (![year, month, day].every((num) => Number.isFinite(num))) return null;

  const isUtc = value.endsWith("Z");
  if (isUtc || tzSign) {
    const utc = Date.UTC(year, month, day, hour, minute, second, ms);
    if (tzSign && tzHour && tzMinute) {
      const offsetMinutes = Number(tzHour) * 60 + Number(tzMinute);
      const offsetMs = offsetMinutes * 60 * 1000;
      const direction = tzSign === "+" ? -1 : 1;
      return new Date(utc + direction * offsetMs);
    }
    return new Date(utc);
  }

  // No zone in the string → backend LocalDateTime is UTC; build a UTC instant so it
  // converts correctly to the viewer's local time on display.
  return new Date(Date.UTC(year, month, day, hour, minute, second, ms));
}

export function parseApiDate(value: unknown): Date | null {
  if (value == null) return null;

  if (value instanceof Date) {
    return isValidDate(value) ? value : null;
  }

  if (typeof value === "number") {
    const ms = value < 10_000_000_000 ? value * 1000 : value;
    const date = new Date(ms);
    return isValidDate(date) ? date : null;
  }

  if (Array.isArray(value)) {
    return parseArrayDate(value);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (/^\d+$/.test(trimmed)) {
      return parseApiDate(Number(trimmed));
    }

    const parsed = new Date(normalizeApiDateString(trimmed));
    if (isValidDate(parsed)) return parsed;

    const manual = parseIsoLike(trimmed);
    if (manual && isValidDate(manual)) return manual;
  }

  return null;
}

// Formats follow the active i18next language unless a locale is passed
// explicitly; the browser's Intl data does the actual localization.
export function formatDate(
  value: unknown,
  options?: Intl.DateTimeFormatOptions,
  locale?: string,
): string {
  const date = parseApiDate(value);
  return date
    ? date.toLocaleDateString(locale ?? i18n.language, options)
    : i18n.t("common.unknown");
}

export function formatDateTime(
  value: unknown,
  options?: Intl.DateTimeFormatOptions,
  locale?: string,
): string {
  const date = parseApiDate(value);
  return date
    ? date.toLocaleString(locale ?? i18n.language, options)
    : i18n.t("common.unknown");
}
